import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, Play, Puzzle } from 'lucide-react';
import Button from '../ui/Button';
import { STAT_CARD_BASE } from '../ui/StatCard';
import simulationService from '../../services/simulationService';
import simulationRunService from '../../services/simulationRunService';
import { showError } from '../../utils/toast';

/**
 * "Run a simulation" — the entry point from a live session.
 *
 * Self-contained on purpose: SessionControlPage is ~3,300 lines and already owns the
 * whole live-activity control surface. This adds one collapsed panel and touches
 * nothing else.
 *
 * Starting a run navigates the trainer to the console, from where they open the
 * separate room screen. Renders nothing at all if the org has no playable simulation,
 * so it cannot become clutter for trainers who don't use them.
 */
const LaunchSimulationPanel = ({ sessionId }) => {
  const navigate = useNavigate();
  const [simulations, setSimulations] = useState([]);
  const [open, setOpen] = useState(false);
  const [starting, setStarting] = useState(null);
  // An unfinished run already attached to this session, if any.
  const [activeRun, setActiveRun] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await simulationService.list();
        // Only ones that can actually be played — a draft with no scenarios would
        // fail at the API and look like a bug to the trainer.
        if (alive) setSimulations((res.simulations || []).filter((s) => !s.awaitingGeneration));
      } catch {
        /* simulations are optional; stay quiet rather than erroring a live session */
      }
    })();
    return () => { alive = false; };
  }, []);

  /**
   * Check once for a run already in progress on this session.
   *
   * Without this a trainer who closed the console tab had no route back, and the only
   * apparent option was to Start again — quietly creating a SECOND run and splitting the
   * room across two. Offering the existing run is both the way back and the guard against
   * that. Stays silent on failure: a lookup problem must not break a live session.
   */
  useEffect(() => {
    if (!sessionId) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await simulationRunService.activeForSession(sessionId);
        if (alive && res?.run) {
          setActiveRun(res.run);
          setOpen(true); // a run in progress is worth surfacing without a click
        }
      } catch {
        /* no-op — absence of a rejoin button is a safe failure mode */
      }
    })();
    return () => { alive = false; };
  }, [sessionId]);

  // Nothing to offer at all — no playable simulations and no run to rejoin.
  if (!simulations.length && !activeRun) return null;

  const start = async (simulationId) => {
    setStarting(simulationId);
    try {
      const res = await simulationRunService.start(simulationId, { mode: 'live', sessionId });
      navigate(`/trainer/simulation-runs/${res.run.id}`);
    } catch (err) {
      showError(err.userMessage || err.message || 'Could not start the simulation');
      setStarting(null);
    }
  };

  return (
    <div className={`${STAT_CARD_BASE} overflow-hidden`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
          <Puzzle size={17} strokeWidth={1.75} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            Run a simulation
          </span>
          <span className="block text-xs text-neutral-500 dark:text-neutral-400">
            Split the room into teams for a scored role-play
          </span>
        </span>
        <ChevronDown
          size={16}
          strokeWidth={2}
          className={`shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="space-y-2 border-t border-neutral-200 px-5 py-4 dark:border-neutral-700/60">
          {/* A run already going on this session. Shown above the launch list so the
              obvious action is to rejoin rather than start a duplicate. */}
          {activeRun && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary-200 bg-primary-50 px-3.5 py-3 dark:border-primary-500/30 dark:bg-primary-500/10">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  {activeRun.title}
                </p>
                <p className="mt-0.5 text-xs text-neutral-600 dark:text-neutral-300">
                  Already running · round {activeRun.roundIndex} · {activeRun.phase}
                </p>
              </div>
              <Button
                variant="primary-filled"
                size="sm"
                onClick={() => navigate(`/trainer/simulation-runs/${activeRun.id}`)}
              >
                Rejoin run
                <ArrowRight size={14} strokeWidth={2} className="ml-1.5" />
              </Button>
            </div>
          )}

          {simulations.map((sim) => (
            <div
              key={sim.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-200 px-3.5 py-3 dark:border-neutral-700/60"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{sim.title}</p>
                <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                  {sim.roundCount} rounds · {sim.functionCount} teams · {sim.industry}
                </p>
              </div>
              <Button
                variant="primary-filled"
                size="sm"
                loading={starting === sim.id}
                disabled={Boolean(starting)}
                onClick={() => start(sim.id)}
              >
                <Play size={14} strokeWidth={2} className="mr-1.5" />
                Start
              </Button>
            </div>
          ))}
          <p className="pt-1 text-[11px] text-neutral-400">
            Starting takes you to the facilitator console. Open the room screen from there —
            your console shows what each team picked, so don&apos;t project it.
          </p>
        </div>
      )}
    </div>
  );
};

LaunchSimulationPanel.propTypes = {
  sessionId: PropTypes.string.isRequired,
};

export default LaunchSimulationPanel;
